import { useCallback, useState, useEffect, useMemo } from "react";
import {
    useFilters,
    useGroupBy,
    useExpanded,
    usePagination,
    useRowSelect,
    useSortBy,
    useTable,
} from "react-table";
import { Card, Table } from "reactstrap";

import DefaultColumnFilter from "components/ReactTable/Components/DefaultColumnFilter/DefaultColumnFilter";
import DimensionSpinner from "components/DimensionSpinner/DimensionSpinner";
import EditIntegrationsBody from "./Components/EditIntegrationsBody";
import ExportCsv from "components/ExportCsv/ExportCsv";
import PageSizeSelect from "components/ReactTable/Components/PageSizeSelect";
import PaginationButtons from "components/ReactTable/Components/PaginationButtons/PaginationButtons";
import Row from "components/ReactTable/Components/Row";
import RunContextMenu from "Pages/Run/Components/RunContextMenu";
import ClientHealthContextMenu from "Pages/Admin/ClientHealth/Components/ClientHealthContextMenu";
import styles from "components/ReactTable/ReactTable.module.css";
import THeadContent from "./Components/THeadContent/THeadContent";
import useContextMenu from "Hooks/useContextMenu";
import { useLocation } from "react-router-dom";

const maxPaginationButtons = 5;
const defaultPageSize = 25;
const defaultFunction = (row) => {};

/** Reusable table built with React Table library.
 * @see https://github.com/TanStack/table/blob/v7/docs/src/pages/docs/api/useTable.md
 *
 * Required:
 * @param columns - Array of column objects. Required. Make the Header property "hidden" to hide the column.
 * @param data - Array of data objects. Required.
 *
 * Optional:
 * @param isLoading - Boolean to show loading spinner. Optional. Defaults to false. A loading state
 * must be set up in the parent and passed down to the table for this to work.
 * @param customStyle - Can pass style object from parent to send down any custom CSS rules to be applied
 * on the table element. Optional. Used in MatchTools.tsx for height.
 * @param onSetSelectedRow - State setting callback passed from parent to enable row selection and
 * actually set the selected row state in the parent. Optional.
 * @param onSetSelectedRows - State setting callback passed from parent to enable multiple row
 * selection. Optional.
 * @param allRowsSelected - Boolean to select all rows. Optional. Used in StuckIntegrations.tsx.
 * @param onToggleAllRowsSelected - Callback passed from parent to pass up the selected rows to parent. Optional.
 * @param onSetDataForDraggable - Callback passed from parent to update data and persist to DB for
 * a draggable table. Makes table rows draggable. Optional. Used in EditIntegrations.tsx.
 * @param rowClickCallback - Row onClick callback function passed from parent. Optional.This and
 * onSetSelectedRow cannot be used together.
 * @param rowDoubleClickCallback - Row onDoublelick callback function passed from parent. Optional.
 * @param isRowClickable - (row) => boolean. Optional, defaults to every row clickable. Rows it
 * rejects get no onClick and no pointer cursor — use it when some rows have nothing to open
 * (e.g. Run.tsx's pending jobs, which are queued job rows with no run behind them yet).
 * @param rowGroupByColumn - String of column accessor to group rows by. Optional. Used in EditIntegrations.tsx.
 * @param showPagination - Boolean to show pagination component defaults false. Optional.
 * @param showContextMenu - Boolean to show context menu defaults false. Optional. Only used in Run.tsx and ClientHealth.tsx.
 * @param showExportCsv - Boolean to show Export CSV button. Optional. Defaults to true.
 * @param triggerGoToPageOne - Boolean to trigger useEffect here when changed to go to page one when true. Optional / defaults to false.
 * Used in Run, run details, and message search to manually go to page one on filter change since autoResetPage is false in useTable.
 * @param serverFetch - Passed down for server side pagination. Optional. Object containing a fetchData fn and 
 * pageCount.
 * @param serverPages - Passed down for server side pagination. Optional.  Object containing pageCount and totalRecords.
*/
export default function ReactTable({
    columns,
    data,
    allRowsSelected = undefined,
    onToggleAllRowsSelected = defaultFunction,
    customStyle = {},
    isLoading,
    onSetDataForDraggable = defaultFunction,
    onSetSelectedRow = defaultFunction,
    onSetSelectedRows = defaultFunction,
    rowClickCallback = defaultFunction,
    rowDoubleClickCallback = defaultFunction,
    // Takes the row — keep the parameter so TS consumers infer (row) => boolean.
    isRowClickable = (row) => true, // eslint-disable-line no-unused-vars
    rowGroupByColumn = "",
    showContextMenu = false,
    showExportCsv = true,
    showPagination = false,
    triggerGoToPageOne = false,
    serverFetch = {},
    serverPages = {},
    /** Stable key for the localStorage pageSize. Defaults to current pathname,
     * which breaks for routes with a dynamic segment (e.g. /run_details/:id —
     * each row gets its own key, so the size never persists across visits).
     * Parents on dynamic routes should pass a static value. */
    pageSizeStorageKey = "",
    /** The data given to the table is a capped window of a larger result. The footer then says
     *  "of 500+" instead of presenting the cap as the total. */
    isTruncated = false
}) {
    const [paginationButtons, setPaginationButtons] = useState([]);
    const { contextMenu, toggle, handleContextMenu } = useContextMenu();
    const { pathname } = useLocation();
    const storageKey = pageSizeStorageKey || pathname;

    const VALID_PAGE_SIZES = [10, 25, 50, 100, 250, 500];
    const savedPageSize = useMemo(() => {
        const saved = localStorage.getItem(`${storageKey}TablePageSize`);
        return saved && VALID_PAGE_SIZES.includes(Number(saved)) ? Number(saved) : defaultPageSize;
    }, [storageKey]);

    const isServerPagination = useMemo(() => Object.keys(serverFetch).length > 0, [serverFetch]);

    /** Add the default column filter to the table */
    const defaultColumn = useMemo(
        () => ({
            Filter: ({ column }) => {
                return <DefaultColumnFilter column={column} />;
            },
        }),
        []
    );

    // React-table setup

    const tableOptions = useMemo(() => {
        const opts = {
            autoResetSortBy: false,
            autoResetFilters: false,
            autoResetExpanded: false,
            autoResetGroupBy: false,
            autoResetPage: false,
            columns,
            data,
            defaultColumn,
            initialState: {
                hiddenColumns: columns
                    .filter((column) => column.Header === "hidden")
                    .map((column) => column.accessor),
                pageIndex: 0,
                pageSize: savedPageSize,
                groupBy: [rowGroupByColumn],
            },
        };

        if (isServerPagination) {
            opts.manualPagination = true;
            opts.pageCount = serverPages.pageCount;
        }

        return opts;
    }, [
        columns,
        data,
        defaultColumn,
        isServerPagination,
        rowGroupByColumn,
        savedPageSize,
        serverPages.pageCount,
    ]);

    // Variables destructured from table instance
    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        prepareRow,
        page,
        canPreviousPage,
        canNextPage,
        pageCount,
        gotoPage,
        nextPage,
        previousPage,
        rows,
        setPageSize,
        selectedFlatRows,
        state: { pageIndex, pageSize, filters },
        toggleAllRowsExpanded,
        toggleAllRowsSelected,
    } = useTable(
        // The useTable hook returns the table instance
        tableOptions,
        useFilters,
        useGroupBy,
        useSortBy,
        useExpanded,
        usePagination,
        useRowSelect
    );

    // Expand all rows on page load if grouped.
    useEffect(() => {
        if (!data || rowGroupByColumn === "") {
            return;
        }

        toggleAllRowsExpanded(true);
    }, [toggleAllRowsExpanded, data, rowGroupByColumn]);

    /** Calculate pagination buttons on page load, when page size, page index, or filters (rows) change */
    const recalculatePagination = useCallback(
        (nextPageSize, nextPageIndex) => {
            const numRecords = isServerPagination
                ? serverPages.totalRecords
                : rows.length;

            let totalPages = Math.ceil(numRecords / nextPageSize);
            let pageNums = Array(Math.min(totalPages, maxPaginationButtons))
                .fill(0)
                .map((x, i) => i);

            if (nextPageSize !== pageSize) {
                // Page size is changing, so relative page index will also change
                nextPageIndex = Math.floor(
                    (nextPageIndex / pageCount) * totalPages
                );
            }

            if (totalPages > maxPaginationButtons) {
                let nextGroup = Math.floor(
                    nextPageIndex / maxPaginationButtons
                );
                let floor = nextGroup * maxPaginationButtons;
                pageNums = Array(
                    Math.min(totalPages - floor, maxPaginationButtons)
                )
                    .fill(0)
                    .map((x, i) => i + floor);
            }
            setPaginationButtons(pageNums);
        },
        [
            isServerPagination,
            serverPages.totalRecords,
            rows.length,
            pageSize,
            pageCount,
        ]
    );

    // Call recalculatePagination when any column filter changes
    useEffect(() => {
        recalculatePagination(pageSize, pageIndex);
    }, [pageIndex, pageSize, recalculatePagination, rows]);

    // Go to first page when filters change, because autoResetPage is false in useTable,
    // but we still want to reset to page 0 when filters change.
    useEffect(() => {
        gotoPage(0);
    }, [filters, gotoPage, triggerGoToPageOne, serverPages.totalRecords]);

    // Set page size to max if showPagination is false
    useEffect(() => {
        if (!showPagination) {
            setPageSize(1000);
        }
    }, [showPagination, setPageSize]);

    // Select / unselect all rows based on parent state. Pass the values of the rows up to parent
    // with onToggleAllRowsSelected.
    useEffect(() => {
        if (allRowsSelected) {
            toggleAllRowsSelected(true);
            onToggleAllRowsSelected(rows);
        } else if (allRowsSelected === false) {
            toggleAllRowsSelected(false);
            onToggleAllRowsSelected([]);
        }
    }, [allRowsSelected, onToggleAllRowsSelected, rows, toggleAllRowsSelected]);

    // If serverPagination is true, fetch data when pageIndex or pageSize changes with the fetch passed down from parent
    useEffect(() => {
        if (!isServerPagination) {
            return;
        }
        serverFetch.fetchData({ pageIndex, pageSize });
    }, [isServerPagination, pageIndex, pageSize, serverFetch]);

    /** If using serverPagination and there is a fetchInterval passed down, 
     * use the fetch interval for polling.
     */
    useEffect(() => {
        if(!isServerPagination) {
            return;
        }
        
        const { fetchData, fetchInterval } = serverFetch;

        let interval;
        if (fetchInterval) {
            interval = setInterval(() => fetchData({pageIndex, pageSize}), fetchInterval);
        } else {
            clearInterval(interval);
        }

        return () => {
            clearInterval(interval);
        };
    }, [isServerPagination, pageIndex, pageSize, serverFetch]);

    /** Fired on row click if applicable. Sets state in parent with
     * onSetSelectedRow passed down from parent. */
    function selectRow(currentRow) {
        const previousSelectedRow = selectedFlatRows[0];
        if (previousSelectedRow) {
            if (previousSelectedRow.index !== currentRow.index) {
                previousSelectedRow.toggleRowSelected(false);
                currentRow.toggleRowSelected();
                onSetSelectedRow(currentRow);
                return;
            } else {
                currentRow.toggleRowSelected(false);
                onSetSelectedRow(undefined);
                return;
            }
        } else {
            onSetSelectedRow(currentRow.isSelected ? undefined : currentRow);
            currentRow.toggleRowSelected();
        }
    }

    /** Handles selecting multiple rows in a table. */
    function selectRows(currentRow) {
        onSetSelectedRows(currentRow);
        currentRow.toggleRowSelected();
    }

    // ===== Drag and drop setup =====
    /** Fired at end of drag/drop. Updates state and calls the callback function
     * from the parent which persists the data with new order / group name to the DB.
     * Used in EditIntegrations.tsx.
     */
    const onDragEnd = (result) => {
        const { source, destination } = result;

        if (!destination) return;

        if (
            source.droppableId === destination.droppableId &&
            source.index === destination.index
        )
            return;

        // Remove the element from the start position
        const newData = [...data];
        const [removed] = newData.splice(source.index, 1);

        // If moving across groups, subtract one from destination index
        // to correct for behavior where row would end up an extra index down
        const destinationIndex =
            source.index < destination.index &&
            removed.integration_group !== destination.droppableId
                ? destination.index - 1
                : destination.index;

        // Set the new integration group
        removed.integration_group = destination.droppableId;

        // Add the record to the new position, update list_order, set state to
        // update UI, then save to database
        newData.splice(destinationIndex, 0, removed);
        newData.map((integration, index) => (integration.list_order = index));
        onSetDataForDraggable(newData);
    };

    /** Convert data for EditIntegrations.tsx to an array by integration_group:
     * [{integration_group: string, rows: [row]}, ...]
     * Used for draggable rows on that page.
     */
    const integration_groups =
        data.length > 0
            ? page.reduce((acc, row) => {
                  if (!row.original) {
                      acc.push({
                          integration_group: row.groupByVal,

                          rows: [row],
                      });
                      return acc;
                  }

                  const group = acc.find(
                      (group) =>
                          group.integration_group ===
                          row.original.integration_group
                  );
                  if (group) {
                      group.rows.push(row);
                  } else {
                      acc.push({
                          integration_group: row.original.integration_group,
                          rows: [row],
                      });
                  }
                  return acc;
              }, [])
            : [];

    const exportCsvProps = {
        headerTitles: columns.map((column) => column.Header),
        rows,
        prepareRow,
    };

    // ===== Props for children (some set here and some inline in JSX) =====
    const pageSizeSelectProps = {
        setPageSize,
        page,
        pageIndex,
        pageSize,
        recalculatePagination,
        totalRecords: isServerPagination
            ? serverPages.totalRecords
            : rows.length,
        storageKey: pageSizeStorageKey,
        isTruncated,
    };

    const paginationButtonsProps = {
        canNextPage,
        canPreviousPage,
        gotoPage,
        nextPage,
        pageCount: isServerPagination ? serverPages.pageCount : pageCount,
        pageIndex,
        pageSize,
        paginationButtons,
        previousPage,
        recalculatePagination,
    };

    return (
        <>
            <Card className={styles.tableBox} style={customStyle}>
                {/* Box above the main table, holds pagination content and export csv button */}
                <div className={styles.tableTopAndBottomBoxes}>
                    {showPagination ? (
                        <>
                            <PageSizeSelect {...pageSizeSelectProps} />
                            {showExportCsv && <ExportCsv {...exportCsvProps} />}
                            <PaginationButtons {...paginationButtonsProps} />
                        </>
                    ) : (
                        showExportCsv && <ExportCsv {...exportCsvProps} />
                    )}
                </div>

                {/* The actual table */}
                <Table
                    id="reactTable"
                    hover
                    striped
                    {...getTableProps()}
                >
                    <thead>
                        <THeadContent headerGroups={headerGroups} />
                    </thead>

                    {/* Render the DimensionSpinner if loading, otherwise the table body */}
                    {isLoading ? (
                        <tbody>
                            <tr>
                                <td colSpan={columns.length}>
                                    <DimensionSpinner />
                                </td>
                            </tr>
                        </tbody>
                    ) : data.length === 0 ? (
                        <tbody>
                            <tr>
                                <td
                                    colSpan={columns.length}
                                    className={styles.noRecordsFound}
                                >
                                    No records found
                                </td>
                            </tr>
                        </tbody>
                    ) : // ===== If these are equal, rows are not draggable, use regular table body =====
                    onSetDataForDraggable === defaultFunction ? (
                        <tbody {...getTableBodyProps()}>
                            {/* Render the rows */}
                            {page.map((row) => {
                                prepareRow(row);

                                return (
                                    <Row
                                        key={row.id}
                                        row={row}
                                        onSetSelectedRow={onSetSelectedRow}
                                        onSetSelectedRows={onSetSelectedRows}
                                        defaultFunction={defaultFunction}
                                        selectRow={selectRow}
                                        selectRows={selectRows}
                                        rowClickCallback={rowClickCallback}
                                        rowDoubleClickCallback={
                                            rowDoubleClickCallback
                                        }
                                        showContextMenu={showContextMenu}
                                        handleContextMenu={handleContextMenu}
                                        isRowClickable={isRowClickable}
                                    />
                                );
                            })}
                        </tbody>
                    ) : (
                        // ===== Otherwise, rows are draggable, use this custom table body =====
                        <EditIntegrationsBody
                            integration_groups={integration_groups}
                            prepareRow={prepareRow}
                            onSetSelectedRow={onSetSelectedRow}
                            onSetSelectedRows={onSetSelectedRows}
                            defaultFunction={defaultFunction}
                            selectRow={selectRow}
                            selectRows={selectRows}
                            rowClickCallback={rowClickCallback}
                            rowDoubleClickCallback={rowDoubleClickCallback}
                            showContextMenu={showContextMenu}
                            handleContextMenu={handleContextMenu}
                            onSetDataForDraggable={onSetDataForDraggable}
                            onDragEnd={onDragEnd}
                        />
                    )}
                </Table>

                {/* Box below the table, holds pagination buttons */}
                <div className={styles.tableTopAndBottomBoxes}>
                    {showPagination && (
                        <PaginationButtons {...paginationButtonsProps} />
                    )}
                </div>
            </Card>

            {/* Context menu, not seen unless right click is fired in Run.tsx */}
            <RunContextMenu
                visible={
                    contextMenu.visible && window.location.pathname === "/run"
                }
                toggle={toggle}
                row={contextMenu.row}
                x={contextMenu.x}
                y={contextMenu.y}
            />

            <ClientHealthContextMenu
                visible={
                    contextMenu.visible &&
                    window.location.pathname === "/admin/client_health/summary"
                }
                toggle={toggle}
                row={contextMenu.row}
                x={contextMenu.x}
                y={contextMenu.y}
            />
        </>
    );
}
